"use client";

import "@vidstack/react/player/styles/base.css";

import type { DASHSrc, HLSSrc } from "@vidstack/react";
import {
  Controls,
  MediaPlayer,
  type MediaPlayerInstance,
  MediaProvider,
  Poster,
  Time,
  TimeSlider,
  VolumeSlider,
} from "@vidstack/react";
import Link from "next/link";
import type { ComponentProps, MouseEvent } from "react";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  LuLayers,
  LuLink2,
  LuVolume,
  LuVolume1,
  LuVolume2,
  LuVolumeX,
} from "react-icons/lu";

import { AddToCollectionDropdown } from "@/components/shared/add-to-collection-dropdown";
import { LocalMediaStorageWithoutVolume } from "@/components/shared/media-player/media-storage";
import {
  Fullscreen,
  Gestures,
  Play,
} from "@/components/shared/media-player/shared";
import { useMediaVolumeCommit } from "@/components/shared/media-player/use-media-volume-commit";
import {
  configureMediaProvider,
  getMediaElementFromProvider,
  preProcessDashManifest,
  useMediaSource,
} from "@/components/shared/media-player/utils";
import { Button } from "@/components/ui/button";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import { Switch } from "@/components/ui/switch";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useLayout } from "@/contexts/layout-context";
import { useMobile } from "@/hooks/use-mobile";
import { isPWA } from "@/utils/helpers";
import { cn } from "@/utils/lib";
import {
  hasValidMediaDimensions,
  isValidMediaDimension,
} from "@/utils/media-dimensions";
import type { VideoMetadata } from "@/utils/media-utils";
import {
  getMediaClientIdFromUrl,
  getMediaMetaFromUrl,
} from "@/utils/post-utils";
import type { PostMediaMeta } from "@/utils/types";

type PostVideoPlayerProps = Omit<
  ComponentProps<typeof MediaPlayer>,
  "autoplay" | "children" | "muted" | "storage" | "volume"
> & {
  src: string;
  mediaMeta?: PostMediaMeta;
  onPlayChange?: (playing: boolean) => void;
  postHash?: string;
  sourceLink?: string;
};

const FALLBACK_LANDSCAPE_VIDEO_DIMENSIONS = {
  width: 1280,
  height: 720,
} as const;

const FALLBACK_PORTRAIT_VIDEO_DIMENSIONS = {
  width: 720,
  height: 1280,
} as const;

const PORTRAIT_PLAYER_MAX_HEIGHT_PX = 640;

type ResolvedVideoDimensions = {
  width: number;
  height: number;
};

const loadedVideoDimensionsCache = new Map<string, ResolvedVideoDimensions>();

const formatCssNumber = (value: number) => {
  return Number(value.toFixed(4));
};

export const PostVideoPlayer = forwardRef<
  MediaPlayerInstance,
  PostVideoPlayerProps
>(function PostVideoPlayer(
  {
    src,
    mediaMeta,
    onPlayChange,
    postHash,
    className,
    sourceLink,
    onProviderChange: onProviderChangeProp,
    onMediaEnterFullscreenRequest: onMediaEnterFullscreenRequestProp,
    onFullscreenChange: onFullscreenChangeProp,
    fullscreenOrientation = "none",
    style,
    ...props
  },
  ref,
) {
  const {
    setPlayingElement,
    mediaVolume,
    setMediaVolume,
    mediaMuted,
    setMediaMuted,
    videoAutoplayEnabled,
    setVideoAutoplayEnabled,
  } = useLayout();
  const { hasMouse } = useMobile();
  const storage = useMemo(
    () => new LocalMediaStorageWithoutVolume("video-player"),
    [],
  );
  const commitMediaVolume = useMediaVolumeCommit({
    mediaVolume,
    setMediaVolume,
  });

  const source = useMediaSource({
    url: src,
    preProcessManifest: preProcessDashManifest,
  });

  const playerRef = useRef<MediaPlayerInstance | null>(null);
  const fullscreenScrollYRef = useRef<number | null>(null);
  const [mediaElement, setMediaElement] = useState<HTMLMediaElement | null>(
    null,
  );
  const [runningAsPwa, setRunningAsPwa] = useState(false);
  const [loadedVideoDimensions, setLoadedVideoDimensions] =
    useState<ResolvedVideoDimensions | null>(
      () => loadedVideoDimensionsCache.get(src) ?? null,
    );
  const metaData = mediaMeta
    ? (getMediaMetaFromUrl(src, mediaMeta) as VideoMetadata)
    : null;
  const duration = metaData?.duration;
  const metadataDimensions =
    isValidMediaDimension(metaData?.width) &&
    isValidMediaDimension(metaData?.height)
      ? {
          width: metaData.width,
          height: metaData.height,
        }
      : null;
  const knownDimensions =
    loadedVideoDimensions && hasValidMediaDimensions(loadedVideoDimensions)
      ? {
          width: loadedVideoDimensions.width,
          height: loadedVideoDimensions.height,
        }
      : metadataDimensions;
  const hasPortraitOrientation = knownDimensions
    ? knownDimensions.height > knownDimensions.width
    : metaData?.orientation === "portrait";
  const { width, height } = knownDimensions
    ? knownDimensions
    : hasPortraitOrientation
      ? FALLBACK_PORTRAIT_VIDEO_DIMENSIONS
      : FALLBACK_LANDSCAPE_VIDEO_DIMENSIONS;
  const portraitPlayerWidth = hasPortraitOrientation
    ? `min(100%, ${formatCssNumber(
        (width / height) * PORTRAIT_PLAYER_MAX_HEIGHT_PX,
      )}px)`
    : undefined;
  const effectiveFullscreenOrientation =
    runningAsPwa && fullscreenOrientation === "none"
      ? "any"
      : fullscreenOrientation;

  useImperativeHandle(ref, () => playerRef.current!, [mediaElement]);

  const setPlayerRef = useCallback((player: MediaPlayerInstance | null) => {
    playerRef.current = player;
  }, []);

  const handleProviderChange = useCallback<
    NonNullable<ComponentProps<typeof MediaPlayer>["onProviderChange"]>
  >(
    (provider, event) => {
      configureMediaProvider(provider);
      setMediaElement(getMediaElementFromProvider(provider));
      onProviderChangeProp?.(provider, event);
    },
    [onProviderChangeProp],
  );
  const handleUserMediaIntent = useCallback(() => {
    const nextVolume = mediaVolume > 0 ? mediaVolume : 1;

    setMediaMuted(false);
    commitMediaVolume(nextVolume, { immediate: true });
    playerRef.current?.remoteControl.unmute();
    playerRef.current?.remoteControl.changeVolume(nextVolume);
  }, [commitMediaVolume, mediaVolume, setMediaMuted]);
  const handleVolumeToggle = useCallback(() => {
    if (mediaMuted || mediaVolume <= 0) {
      handleUserMediaIntent();
      return;
    }

    setMediaMuted(true);
    playerRef.current?.remoteControl.mute();
  }, [handleUserMediaIntent, mediaMuted, mediaVolume, setMediaMuted]);
  const handleMediaVolumeChangeRequest = useCallback(
    (volume: number) => {
      if (volume > 0) {
        setMediaMuted(false);
      } else {
        setMediaMuted(true);
      }

      commitMediaVolume(volume);
    },
    [commitMediaVolume, setMediaMuted],
  );
  const handleAutoplayToggleClick = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      event.preventDefault();
      event.stopPropagation();

      if ((event.target as HTMLElement).closest('[role="switch"]')) {
        return;
      }

      setVideoAutoplayEnabled((enabled) => !enabled);
    },
    [setVideoAutoplayEnabled],
  );
  const handleAutoplayCheckedChange = useCallback(
    (checked: boolean) => {
      setVideoAutoplayEnabled(checked);
    },
    [setVideoAutoplayEnabled],
  );
  const handleMediaEnterFullscreenRequest = useCallback<
    NonNullable<
      ComponentProps<typeof MediaPlayer>["onMediaEnterFullscreenRequest"]
    >
  >(
    (target, event) => {
      fullscreenScrollYRef.current = window.scrollY;
      onMediaEnterFullscreenRequestProp?.(target, event);
    },
    [onMediaEnterFullscreenRequestProp],
  );
  const handleFullscreenChange = useCallback<
    NonNullable<ComponentProps<typeof MediaPlayer>["onFullscreenChange"]>
  >(
    (isFullscreen, event) => {
      if (isFullscreen) {
        fullscreenScrollYRef.current ??= window.scrollY;
      } else {
        const scrollY = fullscreenScrollYRef.current;

        fullscreenScrollYRef.current = null;

        if (scrollY !== null) {
          window.requestAnimationFrame(() => {
            window.scrollTo(0, scrollY);
          });
        }
      }

      onFullscreenChangeProp?.(isFullscreen, event);
    },
    [onFullscreenChangeProp],
  );

  useEffect(() => {
    setRunningAsPwa(isPWA());
  }, []);

  useEffect(() => {
    setLoadedVideoDimensions(loadedVideoDimensionsCache.get(src) ?? null);
  }, [src]);

  useEffect(() => {
    if (!(mediaElement instanceof HTMLVideoElement)) {
      setLoadedVideoDimensions(loadedVideoDimensionsCache.get(src) ?? null);
      return;
    }

    const updateLoadedVideoDimensions = () => {
      const dimensions = {
        width: mediaElement.videoWidth,
        height: mediaElement.videoHeight,
      };

      if (hasValidMediaDimensions(dimensions)) {
        loadedVideoDimensionsCache.set(src, dimensions);
        setLoadedVideoDimensions(dimensions);
      }
    };

    updateLoadedVideoDimensions();
    mediaElement.addEventListener(
      "loadedmetadata",
      updateLoadedVideoDimensions,
    );
    mediaElement.addEventListener("resize", updateLoadedVideoDimensions);

    return () => {
      mediaElement.removeEventListener(
        "loadedmetadata",
        updateLoadedVideoDimensions,
      );
      mediaElement.removeEventListener("resize", updateLoadedVideoDimensions);
    };
  }, [mediaElement, src]);

  const effectiveMuted = mediaMuted || mediaVolume <= 0;

  return (
    <MediaPlayer
      //logLevel={process.env.NODE_ENV !== "production" ? "debug" : "error"}
      ref={setPlayerRef}
      className={cn(
        `
          group relative overflow-hidden rounded-lg border border-border-light
        `,
        hasPortraitOrientation ? "max-h-[640px]" : "max-h-[60vh]",
        className,
      )}
      role="button"
      src={source as HLSSrc | DASHSrc}
      crossOrigin
      playsInline
      viewType="video"
      streamType="on-demand"
      fullscreenOrientation={effectiveFullscreenOrientation}
      load="eager"
      preload="metadata"
      storage={storage}
      aspectRatio={`${width} / ${height}`}
      volume={mediaVolume}
      muted={effectiveMuted}
      duration={duration}
      onPlay={() => {
        setPlayingElement(mediaElement);
        onPlayChange?.(true);
      }}
      onPause={() => {
        setPlayingElement((current) =>
          current === mediaElement ? null : current,
        );
        onPlayChange?.(false);
      }}
      onEnded={() => {
        setPlayingElement((current) =>
          current === mediaElement ? null : current,
        );
        onPlayChange?.(false);
      }}
      onMediaEnterFullscreenRequest={handleMediaEnterFullscreenRequest}
      onFullscreenChange={handleFullscreenChange}
      onMediaVolumeChangeRequest={handleMediaVolumeChangeRequest}
      onProviderChange={handleProviderChange}
      style={{
        pointerEvents: "all",
        ...(hasPortraitOrientation && {
          width: portraitPlayerWidth,
        }),
        ...style,
      }}
      {...props}
    >
      <MediaProvider className="[&>video]:max-h-full [&>video]:max-w-full">
        {source?.poster && (
          <Poster
            className="
              absolute inset-0 block size-full rounded-md object-contain
              opacity-0 transition-opacity
              data-visible:opacity-100
            "
            src={source.poster}
            alt="video poster"
          />
        )}
      </MediaProvider>
      <Gestures onUserMediaIntent={handleUserMediaIntent} />
      <Controls.Root
        className="
          pointer-events-none absolute inset-0 z-10 flex size-full flex-col
          opacity-0 transition-opacity
          data-visible:opacity-100
        "
      >
        <Controls.Group
          className="
            pointer-events-none flex w-full items-center bg-linear-to-b
            from-background to-transparent
          "
        >
          <div
            className="
              flex w-full origin-top -translate-y-4 scale-97 items-center
              justify-end px-2 pt-2 pb-6 transition-transform
              in-data-visible:translate-y-0 in-data-visible:scale-100
            "
          >
            <div
              className="
                pointer-events-auto flex h-8 items-center gap-2
                border border-foreground/10 bg-background/10 pr-1.5 pl-2.5 text-xs
                font-medium text-muted-foreground backdrop-blur-md
              "
              onClick={handleAutoplayToggleClick}
            >
              Autoplay
              <Switch
                checked={videoAutoplayEnabled}
                aria-label="Autoplay videos"
                onCheckedChange={handleAutoplayCheckedChange}
              />
            </div>
          </div>
        </Controls.Group>
        <div className="flex-1" />
        <Controls.Group
          className="
            pointer-events-auto flex w-full items-center bg-linear-to-t
            from-background to-transparent
          "
        >
          <div
            className="
              flex w-full origin-bottom translate-y-4 scale-97 items-center
              gap-2 py-3 pr-3 pl-2 transition-transform
              in-data-visible:translate-y-0 in-data-visible:scale-100
            "
          >
            <div className="relative flex items-center">
              <Play onUserMediaIntent={handleUserMediaIntent} />
            </div>
            <div className="flex min-w-0 grow flex-col gap-1">
              <div
                className="
                  flex items-center gap-4 text-xs text-muted-foreground
                "
              >
                <div
                  className="
                    flex items-center gap-1 font-mono text-xs
                    text-muted-foreground
                  "
                >
                  <Time type="current" />
                  <div>/</div>
                  <Time type="duration" />
                </div>
                <div className="grow">
                  <TimeSlider.Root
                    className="
                      group trnslate-z-0 relative flex h-[5px] w-full
                      cursor-pointer touch-none items-center outline-hidden
                      select-none
                      aria-hidden:hidden
                    "
                  >
                    <TimeSlider.Track
                      className="
                        relative z-0 h-[5px] w-full overflow-hidden rounded-sm
                        bg-muted-foreground/90 ring-sky-400
                        group-data-focus:ring-3
                      "
                    >
                      <TimeSlider.TrackFill
                        className="
                          absolute z-10 h-full w-(--slider-fill) rounded-sm
                          bg-primary will-change-[width]
                        "
                      />
                      <TimeSlider.Progress
                        className="
                          absolute h-full w-(--slider-progress) rounded-sm
                          bg-white/5 will-change-[width]
                        "
                      />
                    </TimeSlider.Track>
                    <TimeSlider.Thumb
                      className="
                        absolute top-1/2 left-(--slider-fill) z-20 size-[15px]
                        -translate-1/2 rounded-full border border-border
                        bg-primary opacity-0 ring-white/40 transition-opacity
                        will-change-[left]
                        group-data-active:opacity-100
                        group-data-dragging:ring-4
                      "
                    />
                  </TimeSlider.Root>
                </div>
                <div className="flex items-center justify-center gap-4 pr-2">
                  <HoverCard>
                    <HoverCardTrigger closeDelay={0} delay={0}>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="
                          flex size-auto flex-col p-0 text-xl
                          text-muted-foreground hover:text-primary
                        "
                        onClick={handleVolumeToggle}
                      >
                        {effectiveMuted && <LuVolumeX />}
                        {!effectiveMuted && mediaVolume > 0.7 && <LuVolume2 />}
                        {!effectiveMuted &&
                          mediaVolume > 0.2 &&
                          mediaVolume <= 0.7 && <LuVolume1 />}
                        {!effectiveMuted && mediaVolume <= 0.2 && <LuVolume />}
                      </Button>
                    </HoverCardTrigger>
                    <HoverCardContent
                      className={cn("hidden h-[80px] w-auto min-w-0", {
                        block: hasMouse,
                      })}
                      align="center"
                      side="top"
                      sideOffset={0}
                    >
                      <VolumeSlider.Root
                        className="
                          group relative flex h-full max-h-[100px] w-[5px]
                          cursor-pointer touch-none items-center outline-hidden
                          select-none
                          aria-hidden:hidden
                        "
                        orientation="vertical"
                      >
                        <VolumeSlider.Track
                          className="
                            relative z-0 h-full w-[5px] rounded-sm bg-border
                            ring-sky-400
                            group-data-focus:ring-3
                          "
                        >
                          <VolumeSlider.TrackFill
                            className="
                              absolute bottom-0 h-(--slider-fill) w-full
                              rounded-sm bg-primary will-change-[height]
                            "
                          />
                        </VolumeSlider.Track>
                        <VolumeSlider.Thumb
                          className="
                            absolute bottom-(--slider-fill) left-1/2 z-20
                            size-[15px] -translate-x-1/2 translate-y-1/2
                            rounded-full border border-border bg-primary
                            ring-white/40 transition-opacity
                            will-change-[bottom]
                            group-data-dragging:ring-4
                          "
                        />
                      </VolumeSlider.Root>
                    </HoverCardContent>
                  </HoverCard>
                  {sourceLink && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Link href={sourceLink}>
                            <LuLink2
                              className="
                                relative z-1 p-0 text-xl text-muted-foreground
                                hover:text-primary
                              "
                            />
                          </Link>
                        </TooltipTrigger>
                        <TooltipContent>Go to Source Post</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                  {postHash && (
                    <div>
                      <AddToCollectionDropdown
                        types={["playlist", "gallery"]}
                        createText="New Playlist or Gallery"
                        itemData={{
                          type: "video",
                          postHash,
                          clientId: getMediaClientIdFromUrl(src),
                        }}
                        align="end"
                      >
                        <LuLayers
                          className="
                            relative z-1 p-0 text-xl text-muted-foreground
                            hover:text-primary
                          "
                        />
                      </AddToCollectionDropdown>
                    </div>
                  )}
                  <Fullscreen onUserMediaIntent={handleUserMediaIntent} />
                </div>
              </div>
            </div>
          </div>
        </Controls.Group>
      </Controls.Root>
    </MediaPlayer>
  );
});
